/**
 * Sellavos varumärkesresurser.
 *
 * Logotypen finns bara på ett ställe: som SVG-markup här. Både appen och
 * exporterna i administrationen använder samma källa, så de kan aldrig
 * glida isär.
 */

export const BRAND_HEADING_STACK =
  "'Outfit', 'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
export const BRAND_BODY_STACK =
  "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

export const BRAND_COLORS = [
  { name: "Djuphav (mörk)", token: "--background (mörkt läge)", hex: "#0c2340", use: "Mörk bakgrund, ordmärke på ljus botten" },
  { name: "Turkos (primär, mörkt läge)", token: "--primary", hex: "#5cbdb9", use: "Symbolen i mörkt läge, accenter" },
  { name: "Havsblå (primär, ljust läge)", token: "--primary", hex: "#2d8a9e", use: "Symbolen i ljust läge, knappar, länkar" },
  { name: "Mellanblå", token: "--card (mörkt läge)", hex: "#1a4a6e", use: "Kort och paneler på mörk botten" },
  { name: "Ljus text", token: "--foreground (mörkt läge)", hex: "#f4fbfd", use: "Ordmärket på mörk botten" },
  { name: "Dämpad text", token: "--muted-foreground", hex: "#45657a", use: "Hjälptexter och etiketter" },
  { name: "Isblå accent", token: "--accent", hex: "#dcecf3", use: "Markeringar och sekundära ytor" },
  { name: "Varning", token: "--destructive", hex: "#b04747", use: "Radera, fel och varningar" },
] as const;

export const BRAND_FONTS = [
  {
    name: "Outfit",
    role: "Logotyp och rubriker",
    weights: "700–800, tajt teckenavstånd",
    sample: "Sellavo",
    css: BRAND_HEADING_STACK,
  },
  {
    name: "Outfit",
    role: "Mellanrubriker och knappar",
    weights: "500–600",
    sample: "Sälj snabbt och snyggt",
    css: BRAND_HEADING_STACK,
  },
  {
    name: "Figtree",
    role: "Brödtext och gränssnitt",
    weights: "400–600",
    sample: "Fota prylen – AI skriver annonsen och föreslår pris.",
    css: BRAND_BODY_STACK,
  },
] as const;

export type BrandConcept = "tag" | "circle-s" | "lens" | "monogram";
export type BrandMark = "combo" | "symbol" | "wordmark";
export type BrandTheme = "dark" | "light" | "white" | "black" | "mono" | "token";

export const CONCEPTS: { value: BrandConcept; label: string; note: string; official: boolean }[] = [
  { value: "tag", label: "Prislapp med S", note: "Huvudlogga – prislapp som lutar framåt.", official: true },
  { value: "circle-s", label: "S i cirkel", note: "Förslag: kompakt cirkelmärke, bra som appikon.", official: false },
  { value: "lens", label: "Kameralins", note: "Förslag: fotot som startar hela flödet.", official: false },
  { value: "monogram", label: "Monogram SV", note: "Förslag: två bokstäver i rundad ruta.", official: false },
];

export const MARKS: { value: BrandMark; label: string }[] = [
  { value: "combo", label: "Symbol + text" },
  { value: "symbol", label: "Bara symbol" },
  { value: "wordmark", label: "Bara ordmärke" },
];

export const THEMES: { value: BrandTheme; label: string }[] = [
  { value: "dark", label: "Färg på mörk botten" },
  { value: "light", label: "Färg på ljus botten" },
  { value: "white", label: "Helvit" },
  { value: "black", label: "Helsvart" },
  { value: "mono", label: "Svartvit" },
];

type Palette = { mark: string; glyph: string; word: string; wordAccent: string; accent: string };

function palette(theme: BrandTheme): Palette {
  switch (theme) {
    case "light":
      return { mark: "#2d8a9e", glyph: "#ffffff", word: "#0c2340", wordAccent: "#2d8a9e", accent: "#0c2340" };
    case "white":
      return { mark: "#ffffff", glyph: "#0c2340", word: "#ffffff", wordAccent: "#ffffff", accent: "#ffffff" };
    case "black":
      return { mark: "#0c2340", glyph: "#ffffff", word: "#0c2340", wordAccent: "#0c2340", accent: "#0c2340" };
    case "token":
      return {
        mark: "var(--primary)",
        glyph: "var(--background)",
        word: "currentColor",
        wordAccent: "var(--primary)",
        accent: "currentColor",
      };
    case "mono":
      return { mark: "#c3ced6", glyph: "#0c2340", word: "#0c2340", wordAccent: "#5b7285", accent: "#5b7285" };
    default:
      return { mark: "#5cbdb9", glyph: "#0c2340", word: "#f4fbfd", wordAccent: "#5cbdb9", accent: "#f4fbfd" };
  }
}

/** Bakgrundsförslag som matchar respektive färgvariant. */
export function suggestedBackground(theme: BrandTheme) {
  return theme === "dark" || theme === "white" ? "#0c2340" : "#ffffff";
}

const SYMBOL_SIZE = 64;

function symbolMarkup(concept: BrandConcept, p: Palette) {
  const font = `font-family="${BRAND_HEADING_STACK}" font-weight="800" text-anchor="middle" dominant-baseline="central"`;

  if (concept === "circle-s") {
    return `
      <g>
        <circle cx="32" cy="32" r="32" fill="${p.mark}" />
        <text x="32" y="34" ${font} font-size="38" letter-spacing="-1" fill="${p.glyph}">S</text>
      </g>`;
  }

  if (concept === "lens") {
    return `
      <g>
        <rect x="0" y="10" width="64" height="46" rx="12" fill="${p.mark}" />
        <circle cx="32" cy="33" r="14" fill="${p.glyph}" opacity="0.18" />
        <circle cx="32" cy="33" r="10" fill="none" stroke="${p.glyph}" stroke-width="4" />
        <circle cx="50" cy="20" r="3" fill="${p.glyph}" opacity="0.75" />
        <path d="M20 10 L26 3 H38 L44 10 Z" fill="${p.mark}" />
      </g>`;
  }

  if (concept === "monogram") {
    return `
      <g>
        <rect x="0" y="0" width="64" height="64" rx="18" fill="${p.mark}" />
        <text x="32" y="34" ${font} font-size="26" letter-spacing="-0.5" fill="${p.glyph}">SV</text>
      </g>`;
  }

  // Prislapp – huvudloggan. S:et ligger på snedden och följer prislappens
  // diagonal, precis som originalet. S:et är skalat ned något så det inte
  // trycker mot prislappens kanter, särskilt i små storlekar som headern.
  return `
      <g>
        <path d="M35 2 H56 A6 6 0 0 1 62 8 V29 A6 6 0 0 1 60.2 33.3 L33.3 60.2 A6 6 0 0 1 24.8 60.2 L3.8 39.2 A6 6 0 0 1 3.8 30.7 L30.7 3.8 A6 6 0 0 1 35 2 Z" fill="${p.mark}" />
        <circle cx="49" cy="15" r="5.5" fill="${p.glyph}" opacity="0.85" />
        <g transform="translate(30 34) scale(0.94) translate(-30 -34)">
          <path d="M36.5 24.5c-3.4-3.2-11.4-3.4-13.4 1.6-1.9 4.8 3.6 6.9 7.6 8.2 4.6 1.5 7.7 4.6 6 8.8-2.1 5.2-10.2 5.1-13.7 1.6"
            fill="none" stroke="${p.glyph}" stroke-width="7" stroke-linecap="round"
            transform="rotate(-45 30 34)" />
        </g>
      </g>`;


}

/* Ordmärkets naturliga bredd med Outfit 800/44px och -1.6 i teckenavstånd.
   Vi sträcker inte texten (inget textLength) – då behålls typsnittets riktiga former. */
const WORD_WIDTH = 142;

function wordmarkMarkup(p: Palette) {
  // Separata textnoder förhindrar att iOS/Safari renderar den färgade delen
  // som en förminskad eller upphöjd tspan. Båda delarna har exakt samma typografi.
  // Vi använder INTE dominant-baseline (tolkas olika i Safari/Chrome) utan
  // placerar baslinjen manuellt så versalerna blir optiskt centrerade i höjdled.
  const type = `font-family="${BRAND_HEADING_STACK}" font-weight="800" font-size="44" letter-spacing="-1.6"`;
  const baseline = 15.4; // ~halva versalhöjden för Outfit 800 @44px
  return `
      <text x="0" y="${baseline}" ${type} fill="${p.word}">Sell</text>
      <text x="69" y="${baseline}" ${type} fill="${p.wordAccent}">avo</text>`;
}




/** Ritytans mått för en given kombination – används för att behålla proportionerna. */
export function contentBox(mark: BrandMark) {
  if (mark === "symbol") return { w: SYMBOL_SIZE, h: SYMBOL_SIZE };
  if (mark === "wordmark") return { w: WORD_WIDTH, h: 54 };
  return { w: SYMBOL_SIZE + 18 + WORD_WIDTH, h: SYMBOL_SIZE };
}

function contentMarkup(mark: BrandMark, concept: BrandConcept, theme: BrandTheme) {
  const p = palette(theme);
  if (mark === "symbol") return symbolMarkup(concept, p);
  if (mark === "wordmark") return `<g transform="translate(0 27)">${wordmarkMarkup(p)}</g>`;
  return `${symbolMarkup(concept, p)}<g transform="translate(${SYMBOL_SIZE + 18} 32)">${wordmarkMarkup(p)}</g>`;
}

export type LogoOptions = {
  mark: BrandMark;
  concept: BrandConcept;
  theme: BrandTheme;
  width: number;
  height: number;
  /** "transparent" eller en hex-färg. */
  background: string;
  /** Marginal i pixlar runt loggan. */
  padding: number;
  /** Rotation i grader. */
  rotation: number;
};

export function defaultLogoOptions(): LogoOptions {
  return {
    mark: "combo",
    concept: "tag",
    theme: "dark",
    width: 1024,
    height: 320,
    background: "#0c2340",
    padding: 48,
    rotation: 0,
  };
}

/** Bygger en komplett, fristående SVG-sträng för valda inställningar. */
export function buildLogoSvg(o: LogoOptions): string {
  const box = contentBox(o.mark);
  const pad = Math.max(0, Math.min(o.padding, Math.floor(Math.min(o.width, o.height) / 2) - 1));
  const innerW = Math.max(1, o.width - pad * 2);
  const innerH = Math.max(1, o.height - pad * 2);
  const bg =
    o.background === "transparent"
      ? ""
      : `<rect width="${o.width}" height="${o.height}" fill="${o.background}" />`;
  const rotate =
    o.rotation % 360 === 0 ? "" : ` transform="rotate(${o.rotation} ${o.width / 2} ${o.height / 2})"`;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${o.width}" height="${o.height}" viewBox="0 0 ${o.width} ${o.height}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Sellavo">
  <title>Sellavo</title>
  ${bg}
  <g${rotate}>
    <svg x="${pad}" y="${pad}" width="${innerW}" height="${innerH}" viewBox="0 0 ${box.w} ${box.h}" preserveAspectRatio="xMidYMid meet" overflow="visible">
      ${contentMarkup(o.mark, o.concept, o.theme)}
    </svg>
  </g>
</svg>`;
}

/** Logotypen som React-komponent. */
export function BrandLogo({
  mark = "combo",
  concept = "tag",
  theme = "dark",
  className,
}: {
  mark?: BrandMark;
  concept?: BrandConcept;
  theme?: BrandTheme;
  className?: string;
}) {
  const box = contentBox(mark);
  const svg = buildLogoSvg({
    mark,
    concept,
    theme,
    width: box.w,
    height: box.h,
    background: "transparent",
    padding: 0,
    rotation: 0,
  });
  return (
    <span
      aria-hidden
      className={className}
      style={{ display: "inline-flex", lineHeight: 0 }}
      dangerouslySetInnerHTML={{
        __html: svg.replace(/width="\d+" height="\d+"/, 'width="100%" height="100%"'),
      }}
    />
  );
}

/**
 * Huvudloggan för sajten – väljer automatiskt färgvariant efter ljust/mörkt läge.
 * Höjden styrs med className på wrappern.
 */
export function SiteLogo({
  mark = "combo",
  className,
  title = "Sellavo",
}: {
  mark?: BrandMark;
  className?: string;
  title?: string;
}) {
  const box = contentBox(mark);
  return (
    <span
      className={className}
      title={title}
      style={{ display: "inline-flex", alignItems: "center", aspectRatio: `${box.w} / ${box.h}` }}
    >
      <span className="sr-only">{title}</span>
      <BrandLogo mark={mark} concept="tag" theme="token" className="h-full w-full" />
    </span>
  );
}

export const BRAND_DOS = [
  "Använd SVG när det går – den blir skarp i alla storlekar.",
  "Håll fri yta runt loggan: minst halva symbolens höjd på alla sidor.",
  "Minsta storlek: 20 px hög symbol, 110 px bred kombinationslogga.",
  "På foto eller rörigt underlag: lägg loggan på en mörk eller ljus platta.",
  "Använd den turkosa varianten på mörk botten och den havsblå på ljus.",
];

export const BRAND_DONTS = [
  "Sträck inte ut eller tryck ihop loggan – lås proportionerna.",
  "Byt inte färg på symbolen till något utanför paletten.",
  "Lägg inte skugga, kontur eller gradient på märket.",
  "Skriv inte om ordmärket i ett annat typsnitt.",
  "Placera inte loggan direkt på en färg med för låg kontrast.",
];

/** Fri yta och minsta storlek, uttryckt i förhållande till symbolens höjd. */
export const BRAND_SPACING = {
  clearSpaceRatio: 0.5,
  minSymbolPx: 20,
  minComboPx: 110,
};
